import 'regenerator-runtime/runtime';
import { call, race, select, take } from 'redux-saga/effects';
import { DoRequestResponse, getEndpointUrl, GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import {
  EinstiegRequest,
  EinstiegResponse,
  Feature,
  Fehler,
  Role,
  Status,
  VermittlerFom, Versicherungsnehmer,
  Vertreter
} from '@ps20/bff-models';
import { App } from '@ps20-frontend/frontend-models';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { CallEffect, ForkEffect, put, SelectEffect } from '@redux-saga/core/effects';
import { PS20Logger } from '@ps20-frontend/logging';
import { DATEFORMAT_SHORT_ISO, isDevUrl, isUwAzam } from '@ps20-frontend/utils';
import { AnyAction } from 'redux';
import { IAppState } from '../localStore';
import { vnAddressInvalid } from '../local.actions';
import { isSdwSession, shouldUseSdwEndpoint } from '../selectors/transientState.selector';
import { UrlParameterLoader } from '../../urlParameterLoader';
import { isProdUrl } from '../../portal';
// NOSONAR

const volljaehrigAlter = 18;
const globalActions = new GlobalActions();

export enum EINSTIEG_REQUEST_EVENTS {
  EINSTIEG_INIT_REQUEST_SUCCESSFUL = 'EINSTIEG_INIT_REQUEST_SUCCESSFUL',
  EINSTIEG_INIT_REQUEST_FAILED = 'EINSTIEG_INIT_REQUEST_FAILED',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* watchFeatureTogglesOnceForEinstiegSaga(): any {
  yield take(GlobalActions.FEATURE_TOGGLE_RECEIVED);
  yield call(einstiegRequested);
  const [succesfullAction, errorAction] = yield race([
    take(EINSTIEG_REQUEST_EVENTS.EINSTIEG_INIT_REQUEST_SUCCESSFUL),
    take(EINSTIEG_REQUEST_EVENTS.EINSTIEG_INIT_REQUEST_FAILED),
  ]);
  if (succesfullAction) {
    yield call(successCallback, succesfullAction);
  } else if (errorAction) {
    yield call(errorCallback, errorAction);
  }
}

export const einstiegRequested = function* (): Generator<SelectEffect | ForkEffect | CallEffect> {
  try {
    const state: IAppState = (yield select()) as IAppState;
    const globalPut = GlobalPut.getInstance();
    const roleSdw = state.transientState.datenhaltung.sdw?.role?.toUpperCase() as Role;
    if (isUwAzam(state.transientState.datenhaltung.kdv.perId, roleSdw) || roleSdw === Role.GER_SDW_UW_USER.toString()) {
      yield call(globalPut.put, globalActions.roleDetermined(roleSdw));
      yield call(globalPut.put, globalActions.einstiegSuccessful({
        versicherungsnehmer: {
          adresse: {}
        } as Versicherungsnehmer,
        vertreter: {
          role: Role.VERTRETER
        } as Vertreter
      } as EinstiegResponse));
      return;
    }
    const id =
      state.transientState.datenhaltung.azamVorgangsId ||
      state.transientState.datenhaltung.kdv.perId;
    const role = state.transientState.role;

    if (id && role !== Role.SPS) {
      const body: EinstiegRequest = {
        id,
      };
      if (isSdwSession(state) && state.transientState.featureToggles[Feature.VTNR_AMISNOW]) {
        body.accountManager = state.transientState.datenhaltung.sdw?.accountManager;
      }
      const globalPut = GlobalPut.getInstance();
      yield call(
        globalPut.put.bind(globalPut),
        globalActions.doRequest({
          appId: App.SHOP,
          body,
          url: getEndpointUrl(shouldUseSdwEndpoint(state), '/privatschutz/api/einstieg'),
          successEvent: EINSTIEG_REQUEST_EVENTS.EINSTIEG_INIT_REQUEST_SUCCESSFUL,
          errorEvent: EINSTIEG_REQUEST_EVENTS.EINSTIEG_INIT_REQUEST_FAILED,
        }),
      );
    } else if (!id && state.transientState.datenhaltung.av.cryptId && role !== Role.SPS) {
      yield call(globalPut.put, globalActions.roleDetermined(role));
      yield call(globalPut.put, globalActions.einstiegSuccessful({
        versicherungsnehmer: {
          adresse: {}
        } as Versicherungsnehmer,
        vertreter: {
          role: Role.VERTRETER
        } as Vertreter
      } as EinstiegResponse));
    }
  } catch (err) {
    PS20Logger.error(
      App.SHOP,
      'Saga error in /api/einstieg, watchFeatureTogglesOnceForEinstiegSaga.einstiegRequested: ' +
        err,
    );
  }
};

const wrongAddressErrorTextId = 'versicherungsnehmerAdresse';
const TextIdStaatsb = 'versicherungsnehmerStaatsbuergerschaft';

type EinstiegSuccessEffects = Generator<CallEffect | AnyAction>;

function determineEinstiegRole(state: IAppState, isSdw: boolean, params: URLSearchParams): Role {
  return (
    isSdw ||
    isUwAzam(
      state.transientState.datenhaltung.kdv.perId,
      state.transientState.datenhaltung.sdw?.role,
    )
      ? state.transientState.datenhaltung.sdw?.role
      : params.get('role')
  )?.toUpperCase() as Role;
}

function* putDeterminedRole(
  successAction: DoRequestResponse<EinstiegResponse>,
  role: Role,
  isSdw: boolean,
  params: URLSearchParams,
): EinstiegSuccessEffects {
  const globalPut = GlobalPut.getInstance();
  if (!isProdUrl()) {
    const vtnr = (
      isSdw && localStorage.getItem('vtnr') ? localStorage.getItem('vtnr') : params.get('vtnr')
    )?.split('/');

    if (role === Role.GER_SDW_UW_USER) {
      yield call(globalPut.put, globalActions.roleDetermined(role));
    } else if (role && vtnr) {
      const vermittlernummer = {
        vertreternummer: vtnr[2],
        geschaeftsstelle: vtnr[1],
        zweigniederlassung: vtnr[0],
        vermittlerFom: VermittlerFom.NONE,
      };
      successAction.payload.vertreter.vermittlernummern = [vermittlernummer];
      successAction.payload.vertreter.defaultVermittlernummer = vermittlernummer;
      yield call(globalPut.put, globalActions.roleDetermined(role));
    }
  } else {
    if (role === Role.GER_SDW_UW_USER) {
      yield call(globalPut.put, globalActions.roleDetermined(role));
    }
  }
}

function* putEinstiegResult(
  successAction: DoRequestResponse<EinstiegResponse>,
  errorTexte: Array<string>,
): EinstiegSuccessEffects {
  const globalPut = GlobalPut.getInstance();
  if (errorTexte.length === 1 && errorTexte.includes(wrongAddressErrorTextId)) {
    yield put(vnAddressInvalid(successAction.payload));
  } else if (errorTexte.length > 0) {
    const params: Record<string, string> = {};
    if (errorTexte.some(t => t === TextIdStaatsb)) {
      params['staatsb'] = createParamStaatsb(successAction);
    }
    yield call(
      globalPut.put,
      globalActions.errorPageWithCustomContentRequested(
        errorTexte.length > 1 ? errorTexte : errorTexte[0],
        undefined,
        params,
      ),
    );
  } else {
    yield call(globalPut.put, globalActions.einstiegSuccessful(successAction.payload));
  }
}

export const successCallback = function* (
  successAction: DoRequestResponse<EinstiegResponse>,
): Generator<CallEffect | SelectEffect | AnyAction> {
  const errorTexte = checkResponseForErrors(successAction);
  const state: IAppState = (yield select()) as IAppState;
  const params = new URLSearchParams(window.location.search);
  const isSdw = isSdwSession(state);
  const role = determineEinstiegRole(state, isSdw, params);
  yield* putDeterminedRole(successAction, role, isSdw, params);
  cleanParametersForNonDevUrls();
  yield* putEinstiegResult(successAction, errorTexte);
};

// URL-Paramter in allen Nicht-Dev-Umgebungen entfernen
export function cleanParametersForNonDevUrls(): void {
  if (isDevUrl()) {
    return;
  }
  const urlParameterLoader = new UrlParameterLoader(location.search);
  history.replaceState(
    {},
    document.title,
    urlParameterLoader.removeQueryStringFromUrl(location.href),
  );
}

function hasKeinVersicherungsnehmer(successAction: DoRequestResponse<EinstiegResponse>): boolean {
  return (
    successAction.payload.versicherungsnehmer &&
    Object.keys(successAction.payload.versicherungsnehmer).length <= 0
  );
}

function hasKeinVertreter(successAction: DoRequestResponse<EinstiegResponse>): boolean {
  return (
    !successAction.payload.vertreter || Object.keys(successAction.payload.vertreter).length <= 0
  );
}

function createParamStaatsb(successAction: DoRequestResponse<EinstiegResponse>): string {
  const actionPayload: EinstiegResponse = successAction.payload;
  const staatsbschaft = actionPayload.versicherungsnehmer?.staatsbschaft;
  if (!staatsbschaft) {
    return '';
  }
  return `"${staatsbschaft.text} (${staatsbschaft.wert})"`;
}

function checkResponseForErrors(successAction: DoRequestResponse<EinstiegResponse>): Array<string> {
  const errorText = [];
  const actionPayload: EinstiegResponse = successAction.payload;

  if (hasKeinVersicherungsnehmer(successAction)) {
    return ['einstiegKeinVN'];
  }

  if (hasKeinVertreter(successAction)) {
    return ['einstiegKeinVertreter'];
  }

  const adresseValidierungsStatus: Status =
    actionPayload.versicherungsnehmer.adresse.validierungsStatus;
  const staatsbschaftValidierungsStatus: Status =
    actionPayload.versicherungsnehmer.staatsbschaftValidierungsStatus;
  const geburtsdatumAsString = actionPayload.versicherungsnehmer.geburtsdatum;
  const juristisch = actionPayload.versicherungsnehmer.juristisch;
  const role: Role = actionPayload.vertreter.role;

  if (adresseValidierungsStatus === Status.NOK) {
    errorText.push(wrongAddressErrorTextId);
  }
  if (staatsbschaftValidierungsStatus === Status.NOK) {
    errorText.push(TextIdStaatsb);
  }
  if (!geburtsdatumAsString && !juristisch) {
    errorText.push('einstiegGeburtsdatum');
  }
  if (isGeburtsdatumMinderjaehrig(geburtsdatumAsString)) {
    errorText.push('einstiegMinderjaehrig');
  }
  if (!role) {
    errorText.push('einstiegRolle');
  }

  return errorText;
}

function isGeburtsdatumMinderjaehrig(geburtsdatumAsString: string): boolean {
  const geburtsdatumAsMoment = dayjs(geburtsdatumAsString, DATEFORMAT_SHORT_ISO, true);
  const isValid = geburtsdatumAsMoment.isValid();
  const age = dayjs().diff(geburtsdatumAsMoment, 'year');
  return isValid && age < volljaehrigAlter;
}

export const errorCallback = function* (
  error: DoRequestResponse<AxiosError>,
): Generator<CallEffect> {
  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put,
    globalActions.einstiegFailed('Fehler beim Einstieg. ' + error.payload.message),
  );
  const invalidDataVnDetail: Array<string> = getInvalidDataPersonError(error);
  if (hasJuristischePersonError(error)) {
    yield call(
      globalPut.put,
      globalActions.errorPageWithCustomContentRequested('versicherungsnehmerJurpers'),
    );
  } else if (invalidDataVnDetail) {
    yield call(
      globalPut.put,
      globalActions.errorPageWithCustomContentRequested(
        'versicherungsnehmerInvalidData',
        invalidDataVnDetail,
      ),
    );
  } else {
    yield call(
      globalPut.put,
      globalActions.errorPageWithCustomContentRequested('einstiegError'),
    );
  }
};

function hasJuristischePersonError(error: DoRequestResponse<AxiosError>): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { detail }: { [p: string]: any } = error.payload.response.data;
  if (error.payload instanceof AxiosError) {
    return (
      error.payload.response &&
      error.payload.response.data &&
      detail === Fehler.JURISTISCHE_PERSON_AMISCRM
    );
  }
  return false;
}

function getInvalidDataPersonError(error: DoRequestResponse<AxiosError>): Array<string> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { detail, additionalInformation }: { [p: string]: any } = error.payload.response.data;
  if (
    error.payload instanceof AxiosError &&
    error.payload.response?.data &&
    detail === Fehler.EINSTIEG_INVALID_VN_DATA_FEHLER
  ) {
    return additionalInformation;
  }
  return null;
}
